import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Grid, Icon, Popup } from "semantic-ui-react";
import VoiceDetailDisplay from "./aaiVoiceDetailDisplay";

import {
  pauseRecording,
  resumeRecording,
  startRecording,
  stopRecording,
  deleteRecording,
  selectRecordingStatus,
  isAudioChunksEmpty,
  selectAudioChunksLength,
  selectTargetData,
  setSnippetDetails,
  setTargetData
} from "../slices/aaiRecordingUtilitySlice";

import "../styles.css";
import "../index.css";

// Simple recording status indicator component
const RecordingStatusIndicator = ({ status }) => {
  // Determine color based on status
  const getStatusColor = () => {
    switch (status) {
      case "recording": return "red";
      case "paused": return "yellow";
      case "stopped": return "green";
      case "completed": return "green";
      default: return "grey";
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      paddingTop: '10px',
      paddingBottom: '10px'
    }}>
      <div style={{
        minWidth: '20px !important',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: getStatusColor(),
        marginRight: '10px'
      }} />
      <span style={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
        {status === "idle" ? "Ready" : status}
      </span>
    </div>
  );
};

/**
 * Simplified recording controls component without ProgressiveTickCircle
 */
const SimplifiedRecordingControls = ({
  voiceDetailObj = {},
  contentText = '',
  hideControls = false,
  disabled = false,
  onStatusChange = null,
  customStyles = {} // New prop for custom styling
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.profile)?.data?.userId || 'unknown';

  const targetData = useSelector(selectTargetData);

  // Selectors for Redux state
  const recordingStatus = useSelector(selectRecordingStatus);
  const audioChunksLength = useSelector(selectAudioChunksLength);
  const audioChunksIsEmpty = useSelector(isAudioChunksEmpty);

  const {
    character: {
      character: { name } = { name: "Default Name" }
    } = {},
    variation = "Default Variation",
    textSnippet = { content: "Default text", textOverride: null },
  } = targetData?.character || {};

  // Initialize recording state only once when component mounts
  useEffect(() => {
    // Only initialize if we have the required data
    if (voiceDetailObj && contentText) {
      // Initialize recording state
      const targetData = {
        character: {
          character: {
            character: {
              uuid: voiceDetailObj.characterId || 'default-character',
              name: voiceDetailObj.characterItem?.name || 'Character'
            }
          },
          variation: voiceDetailObj.variationId || 'Default'
        },
        variation: voiceDetailObj.variationId || 'Default',
        textSnippet: {
          content: contentText
        }
      };

      // Set snippet details
      const snippetDetails = {
        details: {
          values: {
            literaryGroup: 'voice-actor-portfolios',
            litGroupBook: voiceDetailObj.portfolioId || 'default-portfolio'
          }
        },
        snippetValues: {
          values: {
            title: `${voiceDetailObj.characterItem?.name || 'Character'} - Inline Recording`,
            enabled: true
          }
        }
      };

      // Dispatch actions to set up recording state
      dispatch(setTargetData(targetData));
      dispatch(setSnippetDetails(snippetDetails));
    }
  }, [dispatch, voiceDetailObj, contentText]); // Added dependencies

  // Notify parent component when recording status changes
  useEffect(() => {
    if (onStatusChange && (recordingStatus === 'completed' || recordingStatus === 'stopped')) {
      onStatusChange(recordingStatus, window.audioChunksRef);
    }
  }, [recordingStatus, onStatusChange]);

  // Event Handlers
  const handleStartStop = useCallback(() => {
    if (recordingStatus === "recording" || recordingStatus === "paused") {
      dispatch(stopRecording());
    } else {
      dispatch(startRecording());
    }
  }, [dispatch, recordingStatus]);

  const handlePauseResume = useCallback(() => {
    if (recordingStatus === "paused") {
      dispatch(resumeRecording());
    } else if (recordingStatus === "recording") {
      dispatch(pauseRecording());
    }
  }, [dispatch, recordingStatus]);

  const handleCancelReset = useCallback(() => {
    dispatch(deleteRecording());
  }, [dispatch]);

  return (
    <div
      style={{
        // style="background-color: #d9e2e9 !important;border-radius: 5px;padding: 10px;width: 100%;box-sizing: border-box;opacity: 1;"
        backgroundColor: "#d9e2e9 !important",
        borderRadius: "5px",
        padding: "10px",
        width: "100%",
        boxSizing: "border-box",
        opacity: disabled ? 0.7 : 1,
        paddingBottom: '20px'
        // ...customStyles.container
      }}
    >
      <Grid>
        <Grid.Row verticalAlign="middle">
          {/* Status Indicator */}
          <Grid.Column width={4}>
            <RecordingStatusIndicator status={recordingStatus} />
          </Grid.Column>

          {/* Recording Controls */}
          <Grid.Column width={4} textAlign="left">
            {/* Start/Stop Button */}
            <Popup
              content={recordingStatus === "recording" || recordingStatus === "paused" ? "Stop Recording" : "Start Recording"}
              trigger={
                <Button
                  type="button"
                  circular
                  icon
                  color={recordingStatus === "recording" || recordingStatus === "paused" ? "red" : "green"}
                  onClick={handleStartStop}
                  disabled={disabled}
                  style={customStyles.button}
                >
                  <Icon name={recordingStatus === "recording" || recordingStatus === "paused" ? "stop" : "play"} />
                </Button>
              }
            />

            {/* Pause/Resume Button */}
            <Popup
              content={recordingStatus === "paused" ? "Resume Recording" : "Pause Recording"}
              trigger={
                <Button
                  type="button"
                  circular
                  icon
                  color="yellow"
                  disabled={(recordingStatus !== "recording" && recordingStatus !== "paused") || disabled}
                  onClick={handlePauseResume}
                  style={customStyles.button}
                >
                  <Icon name={recordingStatus === "paused" ? "play" : "pause"} />
                </Button>
              }
            />
            <Popup
              content="Cancel and Reset Recording"
              trigger={
                <Button
                  type="button"
                  circular
                  icon
                  color="grey"
                  disabled={recordingStatus === "idle" || disabled}
                  onClick={handleCancelReset}
                  style={customStyles.button}
                >
                  <Icon name="undo" />
                </Button>
              }
            />
          </Grid.Column>

          {/* Voice Detail (compact version) */}
          <Grid.Column width={7}>
            <VoiceDetailDisplay
              voiceDetailObj={voiceDetailObj}
              charSpeechData={targetData}
              promptWidth={4}
              compact={true}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default React.memo(SimplifiedRecordingControls);
